import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import { useQuery } from '@tanstack/react-query'
import { minutesToMilliseconds } from 'date-fns'
import { getRoutedUrl } from '@/common/utils/urls/getRoutedUrl'
import { waitForCondition } from '@/common/utils/waitForCondition'
import { cmsService } from '@/content/services/cms.service'
import { navigationService } from '@/content/services/navigation.service'
import type { CinemaServiceInterface } from './cinema.service.interface'
import { type CmsCinemaElement, CmsCinemaElementKeys } from './cinema.service.models'

export class CinemaService implements CinemaServiceInterface {
    private cinemas = new Map<string, CmsCinemaElement>()
    private closedCinemas = new Map<string, CmsCinemaElement>()
    private loading = false

    public useCinemas = () => {
        // eslint-disable-next-line react-hooks/rules-of-hooks -- this works fine as useCinemas is only called inside React components, which is where hooks are allowed to be used
        const { data: cinemas, isLoading: isLoadingCinemas } = useQuery({
            queryKey: ['CinemaService'],
            staleTime: minutesToMilliseconds(60),
            queryFn: async () => {
                await this.loadCinemas()
                return this.cinemas
            },
        })

        return { cinemas, isLoadingCinemas }
    }

    public async loadCinemas(reload: boolean = false) {
        // Prevent cinemas from being loaded multiple times
        if (this.loading === true) {
            await waitForCondition(() => this.loading === false, 5000, 10)
        }
        this.loading = true

        if (this.cinemas.size === 0 || reload) {
            try {
                const data = await cmsService.loadPageList<CmsCinemaElement>(
                    CmsCinemaElementKeys,
                    navigationService.getPageUrl('Cinemas')
                )

                this.cinemas.clear()

                data.forEach(cmsCinema => {
                    if (cmsCinema.alias === 'cinema' && cmsCinema.vistaCinema) {
                        this.cinemas.set(cmsCinema.vistaCinema.key, cmsCinema)
                    }
                    if (cmsCinema.alias === 'closedCinema' && cmsCinema.vistaCinema) {
                        this.closedCinemas.set(cmsCinema.vistaCinema.key, cmsCinema)
                    }
                })
            } catch (e) {
                getLogger('CinemaService').value.error('Cannot load cinemas', e)
            }
        }

        this.loading = false
    }

    public async getCinemas(reload: boolean = false): Promise<CmsCinemaElement[]> {
        await this.loadCinemas(reload)

        return Array.from(this.cinemas.values())
    }

    public async getClosedCinemas(reload: boolean = false): Promise<CmsCinemaElement[]> {
        await this.loadCinemas(reload)

        return Array.from(this.closedCinemas.values())
    }

    public async getCinemasByIds(cinemaIds: string[], reload: boolean = false): Promise<CmsCinemaElement[]> {
        await this.loadCinemas(reload)

        if (!cinemaIds) {
            return []
        }

        return cinemaIds.map(id => this.cinemas.get(id)).filter(cinema => !!cinema)
    }

    public useCinemaById(id: string) {
        const { cinemas, isLoadingCinemas } = this.useCinemas()
        const cinema = cinemas?.get(id)
        return { cinema, isLoadingCinema: isLoadingCinemas }
    }

    public async navigateToCinemaById(id: string, params?: { [key: string]: string }) {
        await this.loadCinemas()
        if (this.cinemas.has(id)) {
            const routedUrl = getRoutedUrl(this.cinemas.get(id)?.url || '')
            const newLocation = `${routedUrl}${params ? navigationService.getQueryParams(params) : ''}`
            navigationService.goTo(newLocation)
        }
    }

    public async navigateToCinemaByCmsName(name: string, params?: { [key: string]: string }) {
        const id = await this.getCinemaIdByCmsName(name)
        if (id) await this.navigateToCinemaById(id, params)
    }

    /**
     *
     * @param name The CMS name of the cinema
     * @returns Vista Cinema ID
     */
    public async getCinemaIdByCmsName(name: string): Promise<string | null> {
        await this.loadCinemas()

        for (const cinema of this.cinemas) {
            if (cinema[1].name === name) return cinema[0]
        }

        return null
    }

    /**
     * Returns the cinema Id based on the CMS name
     * WARNING: Load the cinemas before calling this
     *
     * @param name The CMS name of the cinema
     */
    public getCinemaIdByCmsNameSync(name: string): string | null {
        for (const cinema of this.cinemas) {
            if (cinema[1].name === name) return cinema[0]
        }

        return null
    }
}

// export const cinemaService = new CinemaService()
